<script setup lang="ts">
import { computed } from "vue";
import { products, collectionGroups, formatPrice, type Product } from "../data/products";
import ProductArt from "./ProductArt.vue";

const props = defineProps<{ activeCategory: string }>();
const emit = defineEmits<{
  (e: "select", id: string): void;
  (e: "open", slug: string): void;
}>();

const filtered = computed<Product[]>(() => {
  if (props.activeCategory === "all") return products;
  return products.filter((p) => p.category === props.activeCategory);
});

const countLabel = computed(() => {
  const n = filtered.value.length;
  return `${n.toString().padStart(2, "0")} pieces`;
});
</script>

<template>
  <section class="collection os1-shell" id="shop">
    <header class="collection__head">
      <div>
        <span class="os1-eyebrow">Volume 01 — Catalog</span>
        <h2 class="collection__title">The full warm-red range</h2>
      </div>
      <div class="collection__filter">
        <button
          :class="['collection__chip', { 'is-active': activeCategory === 'all' }]"
          @click="emit('select', 'all')"
        >
          All <span class="collection__chip-count">{{ products.length }}</span>
        </button>
        <button
          v-for="g in collectionGroups"
          :key="g.id"
          :class="['collection__chip', { 'is-active': activeCategory === g.id }]"
          @click="emit('select', g.id)"
        >
          {{ g.label }}
          <span class="collection__chip-count">
            {{ products.filter((p) => p.category === g.id).length }}
          </span>
        </button>
      </div>
    </header>

    <div class="collection__meta">
      <span>{{ countLabel }}</span>
      <span>·</span>
      <span>Ships in 2–4 days</span>
      <span>·</span>
      <span>Hand-finished in Europe</span>
    </div>

    <div class="collection__grid">
      <article
        v-for="product in filtered"
        :key="product.id"
        class="card"
      >
        <button class="card__art" @click="emit('open', product.slug)" :aria-label="`Open ${product.name}`">
          <img
            v-if="product.image"
            :src="product.image"
            :alt="product.name"
            class="card__image"
            loading="lazy"
            decoding="async"
          />
          <ProductArt v-else :product="product" />
          <span class="card__quick">Quick view</span>
        </button>
        <div class="card__body">
          <div class="card__top">
            <h3 class="card__name">{{ product.name }}</h3>
            <span class="card__price">{{ formatPrice(product.price) }}</span>
          </div>
          <p class="card__subtitle">{{ product.subtitle }}</p>
          <div class="card__row">
            <ul class="card__swatches" :aria-label="`Available colors for ${product.name}`">
              <li
                v-for="s in product.swatches"
                :key="s.name"
                :style="{ background: s.hex }"
                :title="s.name"
                class="card__swatch"
              />
            </ul>
            <span class="card__origin">{{ product.origin }}</span>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<style scoped>
.collection {
  padding-block: clamp(3rem, 6vw, 5rem);
}

.collection__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.collection__title {
  font-size: var(--os1-step-4);
  color: var(--os1-paper);
  margin-top: 0.6rem;
  max-width: 24ch;
}

.collection__filter {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.collection__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  border: 1px solid var(--os1-line);
  border-radius: 999px;
  color: var(--os1-ink-soft);
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  transition: border-color 180ms ease, color 180ms ease, background 180ms ease;
}

.collection__chip:hover {
  border-color: var(--os1-rose);
  color: var(--os1-rose);
}

.collection__chip.is-active {
  background: rgba(255, 70, 50, 0.16);
  border-color: rgba(255, 70, 50, 0.45);
  color: var(--os1-paper);
}

.collection__chip-count {
  font-family: var(--os1-font-mono);
  font-size: 0.62rem;
  letter-spacing: 0;
  color: var(--os1-ink-mute);
}

.collection__meta {
  display: flex;
  gap: 0.7rem;
  margin-top: 1.4rem;
  margin-bottom: 2rem;
  color: var(--os1-ink-mute);
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.collection__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.4rem 1.2rem;
}

.card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.card__art {
  position: relative;
  display: block;
  width: 100%;
  cursor: pointer;
  overflow: hidden;
  border-radius: var(--os1-radius);
  transition: transform 280ms ease;
}

.card__art:hover {
  transform: translateY(-3px);
}

.card__image {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  background: #1a0606;
  border-radius: var(--os1-radius);
}

.card__quick {
  position: absolute;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%) translateY(8px);
  padding: 0.5rem 0.85rem;
  background: rgba(8, 2, 2, 0.78);
  backdrop-filter: blur(8px);
  border: 1px solid var(--os1-line-strong);
  color: var(--os1-paper);
  font-size: 0.62rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  border-radius: 999px;
  opacity: 0;
  transition: opacity 200ms ease, transform 240ms ease;
}

.card__art:hover .card__quick {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.card__top {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.6rem;
}

.card__name {
  font-family: var(--os1-font-serif);
  font-size: 1.05rem;
  color: var(--os1-paper);
}

.card__price {
  font-family: var(--os1-font-mono);
  font-size: 0.85rem;
  color: var(--os1-rose);
}

.card__subtitle {
  color: var(--os1-ink-soft);
  font-size: 0.78rem;
}

.card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-top: 0.25rem;
}

.card__swatches {
  display: flex;
  gap: 0.3rem;
}

.card__swatch {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid rgba(241, 217, 200, 0.18);
}

.card__origin {
  color: var(--os1-ink-mute);
  font-size: 0.65rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}
</style>
